/**
 * Pure toggle rules for the projects grid's multi-select type filter.
 *
 * Lives here rather than inside ProjectsList so the rules can be unit-tested
 * without a render harness — the same split projectPins.js already uses for
 * comparePinnedFirst.
 *
 * INVARIANT: the selection Set holds only real ProjectType values, never the
 * 'all' sentinel. "Show All" is derived at render as `size === 0`, so there is
 * one source of truth and nothing to keep in sync.
 */

/** The `value` of the master "Show All" row in FILTER_OPTIONS. */
export const ALL_TYPES_VALUE = 'all';

/**
 * The selection after clicking a row.
 *
 * @param {Set<string>} current    currently selected ProjectType values
 * @param {string} clicked         the clicked row's value: a ProjectType, or ALL_TYPES_VALUE
 * @param {string[]} allTypeValues every real ProjectType value (excludes ALL_TYPES_VALUE)
 * @returns {Set<string>} a NEW Set; `current` is never mutated
 */
export const nextSelectedTypes = (current, clicked, allTypeValues) => {
  if (clicked === ALL_TYPES_VALUE) return new Set();

  // An empty selection renders as "everything ticked", so the first click on a
  // type row is a SUBTRACTION from everything, not an addition to nothing. This
  // is what makes "show me everything except Visualization" one click.
  if (current.size === 0) {
    return new Set(allTypeValues.filter((v) => v !== clicked));
  }

  const next = new Set(current);
  if (next.has(clicked)) {
    next.delete(clicked);
  } else {
    next.add(clicked);
  }

  // Every type selected filters to the same grid as none selected. Collapse to
  // the empty Set so "Show All" and "3 types" can't be two labels for one state.
  if (next.size === allTypeValues.length) return new Set();

  return next;
};

/**
 * The collapsed pill's text for a selection.
 *
 * A count rather than a comma list: the pill is clamped to `flex: 0 1 8rem` on
 * mobile and sits between the search box and refresh, and "RSA Model, RSA
 * Existing" is nearly twice the width of the longest single label.
 *
 * @param {Set<string>} selectedTypes selected ProjectType values
 * @param {Array<{value: string, label: string}>} options the FILTER_OPTIONS rows
 * @returns {string}
 */
export const filterPillLabel = (selectedTypes, options) => {
  if (selectedTypes.size === 0) return 'Show All';
  if (selectedTypes.size === 1) {
    const [only] = [...selectedTypes];
    return options.find((o) => o.value === only)?.label ?? only;
  }
  return `${selectedTypes.size} types`;
};
